import { observable } from "@legendapp/state";
import type * as Ably from "ably";

import { getOrgChannel } from "~/libs/pubsub.lib";
import { trpc } from "~/libs/trpc.lib";
import { syncedCrudPersist } from "../../libs/legend-state";
import { orgReady$ } from "../app-data-ready";
import type { Catalog } from "./catalogs.types";

/**
 * Debounce window for catalog tree refetches while a sync is in progress.
 * Each schema/table that finishes broadcasts a `catalogs` update — without
 * debouncing, a large catalog would trigger dozens of trpc round-trips per
 * second. The debounce coalesces those into at most one refetch per window.
 */
const PROGRESS_REFRESH_DEBOUNCE_MS = 750;

let progressRefreshTimer: ReturnType<typeof setTimeout> | null = null;

const scheduleProgressRefresh = (refresh: () => void) => {
  if (progressRefreshTimer) clearTimeout(progressRefreshTimer);
  progressRefreshTimer = setTimeout(() => {
    progressRefreshTimer = null;
    refresh();
  }, PROGRESS_REFRESH_DEBOUNCE_MS);
};

export const catalogs$ = observable(
  syncedCrudPersist<Catalog>({
    persist: {
      name: "catalogs",
    },
    waitFor: orgReady$,
    fieldId: "datasourceId",
    list: async () => {
      return await trpc.catalogs.list.query();
    },
    subscribe: ({ refresh, update }) => {
      const channel = getOrgChannel();
      if (!channel) return;

      const seenCompleted = new Set<string>();

      const handler = (message: Ably.Message) => {
        const items = Array.isArray(message.data)
          ? message.data
          : [message.data];

        update({ mode: "merge", value: items });

        // Broadcast payloads only carry the flat `catalogs` row. To show
        // schemas/tables/columns as they stream in we refetch the nested
        // `catalogSchemas → tables → columns` graph: immediately on the
        // first `COMPLETED` for a catalog, debounced for any other
        // RUNNING / post-COMPLETED broadcast (e.g. per-table indexing
        // counter bumps from `catalog-table-process`).
        let firstCompletion = false;
        for (const item of items) {
          if (item?.status === "COMPLETED" && item?.datasourceId) {
            if (!seenCompleted.has(item.datasourceId)) {
              seenCompleted.add(item.datasourceId);
              firstCompletion = true;
            }
          }
        }

        if (firstCompletion) {
          if (progressRefreshTimer) {
            clearTimeout(progressRefreshTimer);
            progressRefreshTimer = null;
          }
          refresh();
        } else if (items.length > 0) {
          scheduleProgressRefresh(refresh);
        }
      };

      channel.subscribe("catalogs", handler);

      return () => {
        channel.unsubscribe("catalogs", handler);
        if (progressRefreshTimer) {
          clearTimeout(progressRefreshTimer);
          progressRefreshTimer = null;
        }
      };
    },
  }),
);
